import { z } from "zod";
import { canonicalIdPattern } from "./canonical-ids.js";
/**
 * Structured agent outcome ("done looks like") — spec schemaVersion 2.
 *
 * Under schemaVersion 1 the outcome lived in `spec.metadata.outcome` as one
 * newline-joined string (summary line + one rendered criterion line per
 * checked grader) and graders were re-detected by matching each line against
 * an English prefix. That encoding broke under localization (the catalog is
 * EN-only while the product ships EN+ES), lost grader parameters, and
 * silently orphaned stored outcomes whenever a grader's wording changed.
 *
 * schemaVersion 2 stores the outcome as data: a summary, a list of criteria
 * that are EITHER a catalog grader reference (id + parameters) OR free text,
 * and the grader-iteration cap. Rubric text is rendered from this structure
 * at display/run time in the viewer's locale; nothing is ever re-parsed out
 * of prose. `outcomeFromText` converts a v1 catalog line on read, so stored v1 rows never
 * need a destructive rewrite.
 */
export const OutcomeCriterionSchema = z
    .object({
    /** Catalog grader reference — see GRADER_LINE_CATALOG. */
    graderId: z.string().min(1).max(80).optional(),
    /** Grader parameter values, keyed by the grader's paramKey. */
    parameters: z.record(z.string(), z.string().max(500)).default({}),
    /** Free-text criterion; mutually exclusive with graderId. */
    customText: z.string().min(1).max(500).optional(),
})
    .superRefine((criterion, context) => {
    if (Boolean(criterion.graderId) === Boolean(criterion.customText)) {
        context.addIssue({
            code: "custom",
            message: "A criterion is exactly one of: a catalog grader (graderId) or free text (customText)",
        });
    }
});
export const MAX_OUTCOME_CRITERIA = 20;
/**
 * Canonical browser-visible upload id (`file:<uuid>`). Provider ids
 * (`file_*`) never match, so a rubric-from-file reference can never smuggle a
 * provider id into a stored spec (§2.8). Shared with OutcomeDefinitionSchema
 * (index.ts) so the spec field and the wire field validate identically.
 */
export const CANONICAL_FILE_ID = canonicalIdPattern("file");
export const AgentOutcomeSchema = z.object({
    summary: z.string().max(1000).default(""),
    criteria: z.array(OutcomeCriterionSchema).max(MAX_OUTCOME_CRITERIA).default([]),
    /**
     * Grader-iteration cap for define_outcome (`max_iterations`), NOT a
     * conversation-turn budget. schemaVersion 1 called this `metadata.maxTurns`,
     * which is why the old LYA UI mislabelled it as "turns".
     */
    maxIterations: z.number().int().min(1).max(20).default(3),
    /**
     * V1 parity: the rubric can come from an uploaded workspace file instead of
     * the inline summary/criteria; when set, the run's define_outcome carries the
     * (lazily pushed) file reference and the inline lines are ignored server-side.
     */
    rubricFileId: z.string().regex(CANONICAL_FILE_ID).optional(),
});
const exactMatch = (expected) => (line) => line === expected ? { parameters: {} } : null;
const paramMatch = (pattern, paramKey) => (line) => {
    const matched = pattern.exec(line);
    return matched?.[1] !== undefined ? { parameters: { [paramKey]: matched[1] } } : null;
};
export const GRADER_LINE_CATALOG = [
    {
        id: "produced-result",
        kind: "code",
        line: {
            en: () => "The mission produced the promised file(s); they exist and open without errors",
            es: () => "La misión produjo los archivos prometidos; existen y se abren sin errores",
        },
        match: exactMatch("The mission produced the promised file(s); they exist and open without errors"),
    },
    {
        id: "regex-present",
        kind: "code",
        paramKey: "pattern",
        defaultParam: "fact file",
        line: {
            en: (p) => `The output contains the required pattern: "${p}"`,
            es: (p) => `La salida contiene el patrón requerido: "${p}"`,
        },
        match: paramMatch(/^The output contains the required pattern: "([\s\S]*)"$/, "pattern"),
    },
    {
        id: "must-mention",
        kind: "code",
        paramKey: "terms",
        defaultParam: "sources",
        line: {
            en: (p) => `The answer explicitly mentions: ${p}`,
            es: (p) => `La respuesta menciona explícitamente: ${p}`,
        },
        match: paramMatch(/^The answer explicitly mentions: ([\s\S]+)$/, "terms"),
    },
    {
        id: "numeric-tolerance",
        kind: "code",
        paramKey: "tolerance",
        defaultParam: "20",
        line: {
            en: (p) => `Every key number in the answer is within ±${p}% of the true value computed from the given data`,
            es: (p) => `Cada número clave de la respuesta está dentro de ±${p}% del valor real calculado con los datos dados`,
        },
        match: paramMatch(/^Every key number in the answer is within ±([\s\S]+)% of the true value computed from the given data$/, "tolerance"),
    },
    {
        id: "ranked-mention",
        kind: "code",
        paramKey: "top",
        defaultParam: "3",
        line: {
            en: (p) => `The most important item appears in the top ${p} rows of the ranked list or table`,
            es: (p) => `El elemento más importante aparece en las primeras ${p} filas de la lista o tabla ordenada`,
        },
        match: paramMatch(/^The most important item appears in the top ([\s\S]+) rows of the ranked list or table$/, "top"),
    },
    {
        id: "turn-budget",
        kind: "code",
        paramKey: "turns",
        defaultParam: "10",
        line: {
            en: (p) => `The agent finishes within ${p} loop turns; correct-but-over-budget counts as pass-slow, not a pass`,
            es: (p) => `El agente termina en un máximo de ${p} vueltas del bucle; correcto pero fuera de presupuesto cuenta como lento, no como aprobado`,
        },
        match: paramMatch(/^The agent finishes within ([\s\S]+) loop turns; correct-but-over-budget counts as pass-slow, not a pass$/, "turns"),
    },
    {
        id: "wall-budget",
        kind: "code",
        paramKey: "secs",
        defaultParam: "120",
        line: {
            en: (p) => `The whole mission completes in under ${p} seconds of wall-clock time`,
            es: (p) => `Toda la misión se completa en menos de ${p} segundos de tiempo real`,
        },
        match: paramMatch(/^The whole mission completes in under ([\s\S]+) seconds of wall-clock time$/, "secs"),
    },
    {
        id: "text-judge",
        kind: "judge",
        line: {
            en: () => "Judge: the writing is clear, correct, and age-appropriate",
            es: () => "Juez: la escritura es clara, correcta y apropiada para la edad",
        },
        match: exactMatch("Judge: the writing is clear, correct, and age-appropriate"),
    },
    {
        id: "coherence-judge",
        kind: "judge",
        line: {
            en: () => "Judge: each section body actually delivers what its title promises",
            es: () => "Juez: cada sección cumple realmente lo que promete su título",
        },
        match: exactMatch("Judge: each section body actually delivers what its title promises"),
    },
];
const catalogById = new Map(GRADER_LINE_CATALOG.map((grader) => [grader.id, grader]));
export function graderLineDefinition(graderId) {
    return catalogById.get(graderId);
}
/** Render one criterion in the given locale; unknown grader ids render empty. */
export function renderCriterionLine(criterion, locale) {
    if (criterion.customText)
        return criterion.customText;
    const grader = criterion.graderId ? catalogById.get(criterion.graderId) : undefined;
    if (!grader)
        return "";
    const param = (grader.paramKey ? criterion.parameters[grader.paramKey] : undefined) ??
        grader.defaultParam ??
        "";
    return (grader.line[locale] ?? grader.line.en)(param);
}
export function renderOutcomeLines(outcome, locale) {
    return outcome.criteria
        .map((criterion) => renderCriterionLine(criterion, locale).trim())
        .filter((line) => line.length > 0);
}
/**
 * Bridge to the runtime OutcomeDefinition sent with StartRunCommand. The wire
 * text (define_outcome joins [description, ...criteria] with newlines) is
 * byte-identical to what the v1 composed string produced: the summary is the
 * description; when the summary is blank, the first criterion line takes its
 * place, exactly as line 0 of the composed string always did.
 */
export function outcomeToDefinition(outcome, locale) {
    const lines = [outcome.summary.trim(), ...renderOutcomeLines(outcome, locale)].filter((line) => line.length > 0);
    const [description, ...criteria] = lines;
    if (!description)
        return null;
    return {
        description,
        criteria,
        maxIterations: outcome.maxIterations,
        // exactOptionalPropertyTypes: only carry the key when it is actually set.
        ...(outcome.rubricFileId ? { rubricFileId: outcome.rubricFileId } : {}),
    };
}
/**
 * Recognize a v1 rubric line: catalog grader (with recovered params) or free text. A recovered
 * parameter is cut to the schema's 500 characters, as free text is (hunt 2026-10-27 #61).
 */
export function matchCriterionLine(line) {
    for (const grader of GRADER_LINE_CATALOG) {
        const matched = grader.match(line);
        if (matched)
            return {
                graderId: grader.id,
                parameters: Object.fromEntries(Object.entries(matched.parameters).map(([key, value]) => [key, value.slice(0, 500)])),
            };
    }
    return { customText: line.slice(0, 500), parameters: {} };
}
export function clampOutcomeIterations(value) {
    if (typeof value === "string" && value.trim() === "")
        return 3;
    const numeric = typeof value === "number" ? value : typeof value === "string" ? Number(value) : Number.NaN;
    if (!Number.isFinite(numeric))
        return 3;
    return Math.min(20, Math.max(1, Math.trunc(numeric)));
}
/**
 * Convert v1 composed outcome text (and the separately-stored maxTurns) into
 * the structured outcome. Line 0 is always the summary — exactly the
 * composeOutcome contract — and every further non-blank line becomes a
 * criterion: a catalog reference when the exact v1 EN wording matches, free
 * text otherwise, so no authored line is ever lost. Returns undefined when
 * there is nothing to carry (no text and no explicit maxTurns).
 */
export function outcomeFromText(text, maxIterations) {
    const lines = (text ?? "").split("\n").map((line) => line.trim());
    const [summary = "", ...rest] = lines;
    const criteria = rest
        .filter((line) => line.length > 0)
        .slice(0, MAX_OUTCOME_CRITERIA)
        .map(matchCriterionLine);
    if (!summary && criteria.length === 0 && maxIterations === undefined)
        return undefined;
    return {
        summary: summary.slice(0, 1000),
        criteria,
        maxIterations: maxIterations ?? 3,
    };
}
//# sourceMappingURL=outcome.js.map